import { createApiRef } from '@backstage/core-plugin-api';
import type { UnifiedGithubApi } from '@runway/devkit';

export type Data = Record<
  string,
  {
    name: string;
    content: string;
    path: string;
  }
>;

export type WorkflowData = {
  name: string;
  content: string;
  path: string;
};

export interface DiffServiceApi {
  formatData(data: WorkflowData[]): Data;
  getCustomerTaxiwayFiles(repo: string, ref: string): Promise<WorkflowData[]>;
  getDTETaxiwayFiles(ref: string): Promise<WorkflowData[]>;
}

export const DiffServiceApiRef = createApiRef<DiffServiceApi>({
  id: 'plugin.diff-tool.service',
});

const DTE_WORKFLOWS = ['js_ci_workflow.yaml', 'python_ci_workflow.yaml', 'maven_ci_workflow.yaml', 'cd_workflow.yaml'];
const GITHUB_ORG = 'AAInternal';

export class DiffService implements DiffServiceApi {
  constructor(private unifiedGithubApi: UnifiedGithubApi) {}

  private fetchWorkflows = async (repo: string, ref: string): Promise<(WorkflowData | null)[]> => {
    return Promise.all(
      DTE_WORKFLOWS.map(async workflow => {
        const path = `.github/workflows/${workflow}`;
        try {
          const data = await this.unifiedGithubApi.getFileInfo({ owner: GITHUB_ORG, repo, filePath: path, ref });
          return data as unknown as WorkflowData;
        } catch {
          return null;
        }
      }),
    );
  };

  getCustomerTaxiwayFiles = async (repo: string, ref: string): Promise<WorkflowData[]> => {
    const files = await this.fetchWorkflows(repo, ref);

    const validFiles: WorkflowData[] = files.filter(file => file !== null) as WorkflowData[];
    if (validFiles.length < 1) {
      throw new Error(
        "Couldn't fetch any Taxiway workflow files. Make sure the file names are correct and/or repo exists.",
      );
    }
    return validFiles;
  };

  getDTETaxiwayFiles = async (taxiwayVersion: string): Promise<WorkflowData[]> => {
    const files = await this.fetchWorkflows('dte-reusable-workflows', taxiwayVersion);

    const validFiles = files.filter(file => file !== null) as WorkflowData[];
    if (validFiles.length < DTE_WORKFLOWS.length) {
      throw new Error("Couldn't fetch all Taxiway workflow files.");
    }

    return validFiles;
  };

  formatData = (data: WorkflowData[]) => {
    const output: Data = {};
    data.map(item => {
      if (item) {
        output[item.path] = {
          name: item.name,
          content: item.content,
          path: item.path,
        };
      } else {
        throw new Error('Item is undefined');
      }
    });
    return output;
  };
}
